// Cross-page toast store. One source of truth for short-lived
// notifications — replaces five copies of the same setTimeout-driven
// flashOk/flashError pattern that crept into the page-level scripts
// while we were focused on backend work.
//
// API is class-based to fit the Svelte 5 Runes pattern: `toast.success(...)`
// from anywhere, `<ToastStack/>` mounted once in App.svelte renders the
// stack. SvelteMap keeps the reactivity automatic.
//
// Phase C ready: later a backend-driven channel ("notifications") can
// feed the same store, so the dashboard's "voice" lands in the same UI
// surface as user-triggered confirmations.

import { SvelteMap } from "svelte/reactivity";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** ms before auto-dismiss; 0 means pinned until manual dismiss. */
  ttl: number;
  /** epoch-ms when the toast was created (used for sort + age display). */
  startedAt: number;
}

class ToastStore {
  #toasts = new SvelteMap<string, Toast>();
  #timers = new Map<string, ReturnType<typeof setTimeout>>();
  #idCounter = 0;

  /**
   * Reactive list, sorted oldest-first. Components consume via
   * `$derived(toast.all)` and re-render when toasts come and go.
   */
  get all(): Toast[] {
    return Array.from(this.#toasts.values()).sort(
      (a, b) => a.startedAt - b.startedAt,
    );
  }

  success(message: string, ttl = 5_000): string {
    return this.#emit("success", message, ttl);
  }

  /** Errors stay longer than success — the user usually wants to read them. */
  error(message: string, ttl = 10_000): string {
    return this.#emit("error", message, ttl);
  }

  info(message: string, ttl = 7_000): string {
    return this.#emit("info", message, ttl);
  }

  dismiss(id: string): void {
    const t = this.#timers.get(id);
    if (t) {
      clearTimeout(t);
      this.#timers.delete(id);
    }
    this.#toasts.delete(id);
  }

  /** Wipe everything; mostly for tests and future "logout" cleanup. */
  clear(): void {
    for (const t of this.#timers.values()) clearTimeout(t);
    this.#timers.clear();
    this.#toasts.clear();
  }

  #nextID(): string {
    this.#idCounter += 1;
    return `t${this.#idCounter}`;
  }

  #emit(kind: ToastKind, message: string, ttl: number): string {
    const id = this.#nextID();
    this.#toasts.set(id, { id, kind, message, ttl, startedAt: Date.now() });
    if (ttl > 0) {
      this.#timers.set(
        id,
        setTimeout(() => this.dismiss(id), ttl),
      );
    }
    return id;
  }
}

export const toast = new ToastStore();
